import { WifiOff, Wifi } from 'lucide-react';
import { useConnection } from '@/contexts/ConnectionContext';

/**
 * ConnectionStatusBar — global, always-mounted connection indicator.
 *
 * Online: small green dot + "Online" label, parked top-right and unobtrusive.
 * Offline: full-width red banner pinned to the top of the viewport so it is
 *          impossible to miss. Tells the user changes cannot be saved until
 *          the connection is restored.
 */
export const ConnectionStatusBar = () => {
  const { isOnline } = useConnection();

  if (!isOnline) {
    return (
      <>
        <div
          className="fixed top-0 left-0 right-0 z-[100] bg-red-600 text-white shadow-lg"
          role="alert"
          aria-live="assertive"
          data-testid="offline-banner"
        >
          <div className="px-3 py-2 sm:px-4 sm:py-2.5 flex items-center justify-center gap-2 text-sm sm:text-[15px] font-medium">
            <WifiOff className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            <span className="text-center">
              You are offline. Changes cannot be saved until your connection is restored.
            </span>
          </div>
        </div>
        {/* Spacer reserves vertical space so the fixed banner does not cover
            sticky headers / first-row content on small screens. */}
        <div aria-hidden="true" className="h-10 sm:h-11" />
      </>
    );
  }

  return (
    <div
      className="fixed top-2 right-3 z-[60] hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/90 backdrop-blur border border-emerald-200 shadow-sm pointer-events-none"
      aria-label="Online"
      data-testid="online-indicator"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
      </span>
      <span className="text-[11px] font-medium text-emerald-700 hidden md:inline-flex items-center gap-1">
        <Wifi className="w-3 h-3" /> Online
      </span>
    </div>
  );
};

export default ConnectionStatusBar;
